import React, { useEffect, useState } from 'react';
import Button from '@material-ui/core/Button';
import Popover from '@material-ui/core/Popover';
import styled, { createGlobalStyle } from 'styled-components';

import { ReactDatePicker } from '@mummssoftware/common-ui';

import {
  isStartDateAllowed,
  rangeFromStart,
  StartDateBounds,
} from '../../utils/dateRange';
import { formatRangeLabel, formatShortDate } from '../../utils/formatters';
import defaults from '../../translations/en.json';

const DATE_FORMAT = 'MM-dd-yyyy';

/**
 * The date picker's calendar portals to #root (portalId, as elsewhere in the
 * repo) while the MUI Popover sits at z-index 1300; without this the calendar
 * opens *behind* the popover. Not unused CSS.
 */
export const CalendarLayer = createGlobalStyle`
  .react-datepicker-popper {
    z-index: 1400 !important;
  }
`;

const Body = styled.div`
  width: 272px;
  padding: 12px 14px 10px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  color: ${(props) => props.theme.palette.text.primary};
`;

const Title = styled.div`
  font-size: 0.8rem;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${(props) => props.theme.palette.text.secondary};
`;

const Row = styled.label`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  font-size: 0.85rem;
`;

const Hint = styled.p`
  margin: 0;
  font-size: 0.75rem;
  color: ${(props) => props.theme.palette.text.secondary};
`;

const ErrorText = styled.p`
  margin: 0;
  font-size: 0.8rem;
  color: ${(props) => props.theme.palette.error?.main || '#f44336'};
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 6px;
  margin-top: 2px;
`;

type StartDatePopoverProps = {
  open: boolean;
  anchorEl: HTMLElement | null;
  /** The committed start date. */
  value: Date;
  bounds: StartDateBounds;
  onApply: (start: Date) => void;
  onClose: () => void;
};

/** Picks where the chat's 365 days of chart data start (locked once the chat starts). */
export const StartDatePopover = ({
  open,
  anchorEl,
  value,
  bounds,
  onApply,
  onClose,
}: StartDatePopoverProps) => {
  // Draft vs committed: the pick lives here until Apply; Cancel discards it.
  const [start, setStart] = useState<Date>(value);

  useEffect(() => {
    if (open) {
      setStart(value);
    }
  }, [open, value]);

  const allowed = isStartDateAllowed(start, bounds);

  const handleApply = () => {
    if (!allowed) {
      return;
    }
    onApply(start);
    onClose();
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
    >
      <CalendarLayer />
      <Body role="dialog" aria-label={defaults.dateRangeTitle}>
        <Title>{defaults.dateRangeTitle}</Title>
        <Row>
          <span>{defaults.from}</span>
          <ReactDatePicker
            portalId="root"
            dateFormat={DATE_FORMAT}
            selectedDate={start}
            minimumDate={bounds.minDate}
            maximumDate={bounds.maxDate}
            onChange={(date: Date | null) => date && setStart(date)}
            inputWidth="8rem"
            ariaLabelledBy={defaults.from}
          />
        </Row>
        {allowed ? (
          <Hint>
            {defaults.dateRangeHint.replace(
              '{range}',
              formatRangeLabel(rangeFromStart(start)),
            )}
          </Hint>
        ) : null}
        <Hint>
          {defaults.dateRangeEarliest.replace(
            '{date}',
            formatShortDate(bounds.minDate),
          )}
        </Hint>
        {allowed ? null : (
          <ErrorText role="alert">
            {defaults.dateRangeErrorStart
              .replace('{min}', formatShortDate(bounds.minDate))
              .replace('{max}', formatShortDate(bounds.maxDate))}
          </ErrorText>
        )}
        <Actions>
          <Button size="small" onClick={onClose}>
            {defaults.cancel}
          </Button>
          <Button
            size="small"
            variant="contained"
            color="primary"
            disabled={!allowed}
            onClick={handleApply}
          >
            {defaults.apply}
          </Button>
        </Actions>
      </Body>
    </Popover>
  );
};

export default StartDatePopover;
